/**
 * useHomeShipments — 홈(대시보드) 출하 위젯의 자료 고르기 (2026-10-22).
 *
 * 공간의 사업이 경원 아스콘(biz "ascon")이면 OKIS 미러(tenants/gyeongwon/units/ascon/…, 톤),
 * 그 밖(중앙 레미콘·옛 주소)은 지금까지와 같은 중앙 자료(RQC·daily_shipments, ㎥).
 * 위젯 부품은 그대로 두고 자료만 바꾼다 — 두 쪽 훅을 늘 부르되 쓰지 않는 쪽은 끈다("" / 0 / enabled false).
 *
 * 경원 숫자 기준(경원 출하조회·출하실적과 같음, AGENTS §4-8):
 *   오늘 = 수주별 수주량(계획 자리 — 실적 아님)·출하누계(계근 실중량)·잔량(asconOrdersToCardRows)
 *   월별·일별 실적 = 수주 출하누계를 관급(A002·A034)·사급으로(asconMonthBuckets·asconDailyShipments)
 */
import { useMemo } from "react";
import { useSpace } from "@/hooks/useSpace";
import { useRqcShipments } from "@/hooks/useRqcShipments";
import { useLastShipment, type LastShipment } from "@/hooks/useLastShipment";
import { useAsconShipments } from "@/hooks/useAsconShipments";
import { useMonthlyShipments, type MonthlyBucket } from "@/hooks/useMonthlyShipments";
import { useAsconMonthly } from "@/hooks/useAsconMonthly";
import { useDailyShipmentsMonth } from "@/hooks/useDailyShipmentsMonth";
import { useAsconYears } from "@/hooks/useAsconYears";
import { kstToday, type RqcShipment } from "@/domain/rqc";
import { asconDailyShipments, asconOrdersToCardRows, type AsconOrder, type AsconTicket } from "@/domain/asconShipment";
import type { DailyShipment } from "@/domain/daily_shipments";
import { shortCarNo } from "@/pages/Dispatch/ascon/carNo";

export interface HomeShipKind {
  /** 경원 아스콘 공간 */
  ascon: boolean;
  /** 물량 단위 글자 — "㎥" / "톤" */
  unit: string;
  /** 계획 수량 이름 — "예정" / "수주" */
  planLabel: string;
  /** 회전수 자리 이름 — "회전" / "계근" */
  tripsLabel: string;
}

const RMC: HomeShipKind = { ascon: false, unit: "㎥", planLabel: "예정", tripsLabel: "회전" };
const ASCON: HomeShipKind = { ascon: true, unit: "톤", planLabel: "수주", tripsLabel: "계근" };

export function useHomeShipKind(): HomeShipKind {
  const { biz } = useSpace();
  return biz === "ascon" ? ASCON : RMC;
}

export interface HomeTodayState extends HomeShipKind {
  /** 오늘 행 — 중앙 RQC 행, 경원은 수주를 같은 모양으로 옮긴 것 */
  shipments: RqcShipment[];
  loading: boolean;
  error: string | null;
  lastUpdated: string;
  lastUpdatedAt: Date | null;
  refresh: () => void;
  lastShipment: LastShipment | null;
  /** 경원 원본(MO 카드 규격 칩용). 중앙이면 빈 배열 */
  orders: AsconOrder[];
  tickets: AsconTicket[];
  /** 경원 가상 자료(?ascon=mock) */
  mock: boolean;
}

const EMPTY_ROWS: RqcShipment[] = [];

/** 오늘 출하. enabled=false 면 어느 쪽도 읽지 않는다(권한 없는 KPI 등) */
export function useHomeToday(enabled = true): HomeTodayState {
  const kind = useHomeShipKind();
  const today = kstToday();
  const rqc = useRqcShipments(enabled && !kind.ascon ? today : "");
  const rqcLast = useLastShipment(enabled && !kind.ascon ? today : "");
  const ac = useAsconShipments(enabled && kind.ascon ? today : "");
  const acRows = useMemo(
    () => (kind.ascon ? asconOrdersToCardRows(ac.orders, ac.tickets) : EMPTY_ROWS),
    [kind.ascon, ac.orders, ac.tickets],
  );
  const acLast = useMemo<LastShipment | null>(() => {
    const last = ac.tickets.reduce<AsconTicket | null>((a, t) => (!a || t.time > a.time ? t : a), null);
    return last ? { time: last.time, carNo: shortCarNo(last.carNo) } : null;
  }, [ac.tickets]);

  if (kind.ascon) {
    return {
      ...kind, shipments: acRows, loading: ac.loading, error: ac.error, lastUpdated: ac.lastUpdated, lastUpdatedAt: ac.lastUpdatedAt,
      refresh: ac.refresh, lastShipment: acLast, orders: ac.orders, tickets: ac.tickets, mock: ac.source === "mock",
    };
  }
  return {
    ...kind, shipments: rqc.shipments, loading: rqc.loading, error: rqc.error, lastUpdated: rqc.lastUpdated, lastUpdatedAt: rqc.lastUpdatedAt,
    refresh: () => void rqc.refresh(), lastShipment: rqcLast, orders: [], tickets: [], mock: false,
  };
}

/** 그 해 1~12월 출하(실적). year 0 = 끔. 경원은 톤이 govM3·custM3·totalM3 칸에 담긴다(칸 이름만 m3) */
export function useHomeMonthly(year: number): { monthly: MonthlyBucket[]; loading: boolean } {
  const { ascon } = useHomeShipKind();
  const rmc = useMonthlyShipments(ascon ? 0 : year);
  const ac = useAsconMonthly(ascon ? year : 0);
  const acMonthly = useMemo<MonthlyBucket[]>(
    () => ac.buckets.map((b) => ({
      ym: b.ym, month: Number(b.ym.slice(5, 7)), govM3: b.govT, custM3: b.custT, totalM3: b.totalT,
    })),
    [ac.buckets],
  );
  if (!ascon) return { monthly: rmc.monthly, loading: rmc.loading };
  // 경원 첫 응답 전에도 1~12월 칸은 보이게(중앙 훅과 같은 모양)
  const monthly = acMonthly.length ? acMonthly : emptyYear(year);
  return { monthly, loading: ac.loading };
}

function emptyYear(year: number): MonthlyBucket[] {
  return Array.from({ length: 12 }, (_, i) => ({
    ym: `${year}-${String(i + 1).padStart(2, "0")}`, month: i + 1, govM3: 0, custM3: 0, totalM3: 0,
  }));
}

interface MonthMaps {
  /** 일 → 실적 합(출하 0 인 날은 키 없음) */
  byDay: Map<number, number>;
  /** 일 → 그날 집계(실적 0 이어도 예정·수주가 있으면 있음) */
  byDayDoc: Map<number, DailyShipment>;
}

/** 캘린더 위젯 — 그 달 일별 집계. 경원은 그 해 수주·계근을 한 번 읽어 일별로(asconDailyShipments, trips = 계근 건수) */
export function useHomeShipMonth(year: number, month: number, enabled: boolean): MonthMaps {
  const { ascon } = useHomeShipKind();
  const rmc = useDailyShipmentsMonth(year, month, enabled && !ascon);
  const years = useMemo(() => (enabled && ascon ? [year] : []), [enabled, ascon, year]);
  const ac = useAsconYears(years);
  const acMaps = useMemo<MonthMaps>(() => {
    const byDay = new Map<number, number>();
    const byDayDoc = new Map<number, DailyShipment>();
    if (!ascon) return { byDay, byDayDoc };
    const prefix = `${year}-${String(month).padStart(2, "0")}-`;
    for (const [date, d] of asconDailyShipments(ac.orders, ac.tickets)) {
      if (!date.startsWith(prefix)) continue;
      const day = Number(date.slice(8, 10));
      byDayDoc.set(day, d);
      if (d.total_m3 > 0) byDay.set(day, d.total_m3);
    }
    return { byDay, byDayDoc };
  }, [ascon, ac.orders, ac.tickets, year, month]);
  return ascon ? acMaps : { byDay: rmc.byDay, byDayDoc: rmc.byDayDoc };
}
